import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import TranslationWrapper from "../../components/TranslationWrapper/TranslationWrapper";
import { FormProvider, useForm } from "react-hook-form";
import GridItem from "../../components/Grid/GridItem";
import GridContainer from "../../components/Grid/GridContainer";
import Button from "../../components/CustomButtons/Button";
import { CircularProgress } from "@mui/material";
import { useRouteLeavingGuard } from "../../components/Forms/RouteLeavingGuard";
import { _get, _request } from "../../http";
import { toast } from "react-toastify";
import "./JamezzFormV2Fix.css";
import _ from "lodash";
import { useNavigate, useParams } from "react-router";

// Map RHF's dirtyFields over the `data` received by `handleSubmit` and return the changed subset of that data.
export function dirtyValues(dirtyFields, allValues) {
  // If *any* item in an array was modified, the entire array must be submitted, because there's no way to indicate
  // "placeholders" for unchanged elements. `dirtyFields` is `true` for leaves.
  // if (dirtyFields === true || Array.isArray(dirtyFields)) return allValues;
  if (dirtyFields === true) return allValues;
  // Here, we have an object
  return Object.fromEntries(
    Object.keys(dirtyFields).map((key) => [key, dirtyValues(dirtyFields[key], allValues[key])])
  );
}

function FormV2(props) {
  const [defaultValues, setDefaultValues] = useState(props.defaultValues ?? {});
  const { id } = useParams();
  const navigate = useNavigate();

  const modelId = useMemo(() => {
    if (id) {
      return id;
    } else if (props.modelId) {
      return props.modelId;
    } else {
      return null;
    }
  }, [id, props.modelId]);
  const [loading, setLoading] = useState(true);
  const changeLogUpdatedAt = useRef(new Date().toString());

  const model = useRef(null);

  const useFormMethods = useForm({
    reValidateMode: props.revalidateMode,
    mode: props.mode,
    defaultValues: defaultValues,
    criteriaMode: "all",
  });

  const {
    formState: { isDirty, isValid, dirtyFields, errors },
    reset,
    trigger,
    setError,
    control,
    getValues,
  } = useFormMethods;

  useRouteLeavingGuard({ when: isDirty });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const endPoint = useMemo(() => {
    if (modelId) {
      return props.endPoint + `/${modelId}`;
    } else {
      return props.endPoint;
    }
  }, [props.endPoint, modelId]);

  const constructDefaultValues = useCallback(
    (response, defaultValues = {}, fieldNamePrefix = "") => {
      if (props.defaultValues && Object.keys(defaultValues).length === 0) {
        defaultValues = { ...props.defaultValues };
      }
      if (response) {
        Object.keys(response).forEach((fieldName) => {
          const formFieldName = fieldNamePrefix + fieldName;

          let value = response[fieldName];
          if (props.parseInput?.[formFieldName]) {
            value = props.parseInput[fieldName](value, defaultValues);
          }
          // if (props.parseExtraInputFields?.[formFieldName]) {
          //   const extraInput = props.parseExtraInputFields[fieldName](value);
          //   if (extraInput) {
          //     Object.keys(extraInput).forEach((key) => {
          //       defaultValues[key] = extraInput[key];
          //     });
          //   }
          // }

          if (typeof value == "object" && value != null && props.objectsExcludedAsRelation?.[fieldName] == false) {
            constructDefaultValues(value, defaultValues, formFieldName + "__");
          } else {
            defaultValues[formFieldName] = value;
          }
        });

        if (defaultValues["media_urls"]) {
          defaultValues.img_uuids = _.map(defaultValues["media_urls"]["imgid"], (mediaObject) => {
            return { uuid: mediaObject.id };
          });
          defaultValues.meal_configurator_layers = _.map(
            defaultValues["media_urls"]["meal_configurator_layers"],
            (mediaObject) => {
              return { uuid: mediaObject.id };
            }
          );
        }

        if (fieldNamePrefix.length === 0) {
          Object.keys(props.extraFields).forEach((extraFieldName) => {
            defaultValues[extraFieldName] = response[extraFieldName];
          });
          model.current = response;
          if (props.onModelReady) {
            props.onModelReady(model.current);
          }
          setDefaultValues(defaultValues);
        }
      }
    },
    [setDefaultValues, control]
  );

  useEffect(() => {
    reloadDataFromServer();
  }, []);

  useEffect(() => {
    if (props.visibleViewIndex) {
      trigger();
    }
  }, [props.visibleViewIndex]);

  const reloadDataFromServer = () => {
    if (modelId != null) {
      _get(endPoint).then((response) => {
        if (props.onGetModel) {
          props.onGetModel(response.data.data);
        }

        constructDefaultValues(response.data.data);

        setLoading(false);
      });
    }
  };

  useEffect(() => {
    if (modelId == null || (defaultValues && Object.keys(defaultValues).length > 0)) {
      reset({ ...defaultValues });
      if (modelId == null) {
        setLoading(false);
      }

      setTimeout(() => {
        if (modelId == null && defaultValues.id) {
          if (props.getEditLink) {
            const url = props.getEditLink(defaultValues);
            if (url) {
              navigate(url);
            }
          } else {
            navigate(`../${defaultValues.id}/edit`);
          }
        } else {
          trigger();
        }
      }, 500);
    }
  }, [defaultValues]); // Do NOT add modelId!!

  const addValue = useCallback(
    (dirtyData, fieldName, value, parseOutputFieldName = null) => {
      if (props.parseOutput?.[parseOutputFieldName ?? fieldName]) {
        value = props.parseOutput[parseOutputFieldName ?? fieldName](
          value,
          control._defaultValues[parseOutputFieldName ?? fieldName],
          dirtyData
        );
      }

      if (value != null && typeof value === "object" && (!value) instanceof File && (!value) instanceof FileList) {
        value = JSON.stringify(value);
      } else if (value === null) {
        value = "";
      } else if (value === true || value === false) {
        value = value ? 1 : 0;
      } else if (value instanceof FileList) {
        value = value[0];
      }
      if (value !== undefined) {
        dirtyData.set(fieldName, value);
      }
    },
    [props.parseOutput]
  );

  const onSubmit = useCallback(() => {
    //trigger validation on fields
    trigger().then((value) => {
      if (value) {
        const dirtyData = new FormData();
        let dirtyFields2 = dirtyFields;

        function checkObject(object) {
          Object.keys(object).forEach((fieldName) => {
            if (!object[fieldName]) {
              delete object[fieldName];
            } else if (typeof object[fieldName] === "object" && !(object[fieldName] instanceof File)) {
              checkObject(object[fieldName]);

              if (Object.keys(object[fieldName]).length === 0) {
                delete object[fieldName];
              }
            } else if (Array.isArray(object[fieldName])) {
              object[fieldName].forEach((item) => {
                checkObject(item);
              });
            }
          });
        }

        checkObject(dirtyFields2);
        if (modelId == null) {
          dirtyFields2 = getValues();
        }
        Object.keys(dirtyFields2).forEach((fieldName) => {
          let values = getValues(fieldName);
          if (values === undefined) {
            return;
          }
          if (props.excludedKeysInOutput?.[fieldName]) {
            return;
          }

          if (!Array.isArray(values)) {
            addValue(dirtyData, fieldName, values);
          } else {
            if (props.customFieldSerialization?.[fieldName]) {
              dirtyData.set(fieldName, props.customFieldSerialization[fieldName](values));
            } else {
              if (values.length == 0) {
                addValue(dirtyData, `${fieldName}`, [], fieldName);
              } else {
                values.forEach((value, index) => {
                  if (typeof value === "object") {
                    Object.keys(value).forEach((key) => {
                      addValue(dirtyData, `${fieldName}__${key}[${index}]`, value[key], fieldName);
                    });
                  } else {
                    addValue(dirtyData, `${fieldName}[${index}]`, value, fieldName);
                  }
                });
              }
            }
          }
        });

        Object.keys(props.extraFields).forEach((fieldName) => {
          dirtyData.set(fieldName, props.extraFields[fieldName]);
        });

        if (props.onSubmit) {
          props.onSubmit(dirtyData);
        }

        _request(modelId ? "patch" : "post", endPoint, dirtyData, null)
          .then((response) => {
            setIsSubmitting(false);
            if (response.data.forceRedirect) {
              window.location.href = response.data.forceRedirect;
            } else {
              if (props.onGetModel) {
                props.onGetModel(response.data.data);
              }
              let item = response.data.data;

              changeLogUpdatedAt.current = new Date().toString();
              constructDefaultValues(item);

              if (props.afterSubmit) {
                props.afterSubmit({ error: false, item: item });
              }
            }
          })
          .catch((error) => {
            setIsSubmitting(false);

            if (error.data?.data?.errors) {
              Object.keys(error.data.data.errors).forEach((fieldName) => {
                error.data.data.errors[fieldName]?.forEach((message) => {
                  toast.error(message);
                  setError(fieldName, { type: "manual", message });
                });
              });
            }

            if (props.afterSubmit) {
              props.afterSubmit({ error: true });
            }
          });
      } else {
        setIsSubmitting(false);
        toast.error("Error, check input fields!");
      }
    });
  }, [dirtyFields, getValues, modelId]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <FormProvider {...props.extraFormProps} {...useFormMethods} reloadDataFromServer={reloadDataFromServer}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isDirty && isValid && !isSubmitting && (errors == null || Object.keys(errors).length === 0)) {
            setIsSubmitting(true);
            onSubmit();
          }
        }}
      >
        {props.showSaveButton ? (
          <GridContainer
            style={{
              position: props.isSaveButtonSticky ? "sticky" : undefined,
              top: props.isSaveButtonSticky ? 0 : undefined,
              zIndex: props.isSaveButtonSticky ? 200 : undefined,
              background: props.isSaveButtonSticky ? "#eee" : undefined,
            }}
          >
            <GridItem>
              <Button
                color={isValid && (errors == null || Object.keys(errors).length === 0) ? "success" : "danger"}
                disabled={!(isDirty && isValid && (errors == null || Object.keys(errors).length === 0)) || isSubmitting}
                id={"FormV2-submitButton-element"}
                onClick={() => {
                  setIsSubmitting(true);
                  onSubmit();
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={18} />
                    <TranslationWrapper tDefault={"Bezig"} tKey={"global.savebutton.bezig.text"} />
                    ...
                  </>
                ) : (
                  <TranslationWrapper tDefault={"Opslaan"} tKey={"global.jamezzForm.savebutton.text"} />
                )}
              </Button>
            </GridItem>
          </GridContainer>
        ) : null}

        {props.children}
      </form>
    </FormProvider>
  );
}

FormV2.defaultProps = {
  primaryKey: "id",
  tabs: [],
  extraFields: {},
  isSaveButtonSticky: true,
  revalidateMode: "onChange",
  mode: "onChange",
  extraFormProps: {},
  objectsExcludedAsRelation: {},
  showSaveButton: true,
};

FormV2.propTypes = {
  endPoint: PropTypes.string.isRequired,
  pageTitle: PropTypes.string.isRequired,
  tabs: PropTypes.array.isRequired,
  primaryKey: PropTypes.string,
  extraFields: PropTypes.object,
  parseInput: PropTypes.object,
  parseOutput: PropTypes.object,
  customFieldSerialization: PropTypes.object,
  onGetModel: PropTypes.func,
  afterSubmit: PropTypes.func,
  revalidateMode: PropTypes.string,
  mode: PropTypes.string,
  extraFormProps: PropTypes.object,
  objectsExcludedAsRelation: PropTypes.object,
  excludedKeysInOutput: PropTypes.object,
  showSaveButton: PropTypes.bool,
  isSaveButtonSticky: PropTypes.bool,
};

export default FormV2;
